import React from 'react';
import type { PassportEncounter } from '../../services/api/passport';
import { formatEncounterDate, encounterStatusLabel, verificationLevelLabel } from './passportCopy';
import type { PassportFeedItem, PassportFeedSection } from './passportFeedUtils';
import CollectorCard from './CollectorCard';
import { encounterCard } from './sourceCards';
type Props = {
  sections: PassportFeedSection[];
  onOpen?: (publicId: string, subjectType: string) => void;
};
function EncounterCard({
  encounter,
  onOpen,
}: {
  encounter: PassportEncounter;
  onOpen?: Props['onOpen'];
}) {
  return (
    <CollectorCard
      card={encounterCard(encounter)}
      href={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
      onOpen={() => onOpen?.(encounter.public_id, encounter.subject_type)}
      openLabel="View encounter"
      fullRecordExtension={
        <section>
          <p>
            {encounterStatusLabel(encounter.status)} · {formatEncounterDate(encounter.occurred_at)}
          </p>
          <p>{verificationLevelLabel(encounter.verification_level)}</p>
          {encounter.memory_caption && <p>{encounter.memory_caption}</p>}
        </section>
      }
    />
  );
}
function FeedItem({ item, onOpen }: { item: PassportFeedItem; onOpen?: Props['onOpen'] }) {
  if (item.kind !== 'vehicle_batch')
    return <EncounterCard encounter={item.encounter} onOpen={onOpen} />;
  return (
    <section className="col-span-full">
      <h4 className="mb-3 text-sm text-zinc-300">
        {item.title} · {formatEncounterDate(item.encounters[0]?.occurred_at)}
      </h4>
      <div className="collector-shelf">
        {item.encounters.map((encounter) => (
          <EncounterCard key={encounter.public_id} encounter={encounter} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}
export const PassportActivityFeed: React.FC<Props> = ({ sections, onOpen }) => {
  if (sections.length === 0) return null;

  return (
    <div className="space-y-10">
      {sections.map((section) => (
        <section key={section.key} aria-labelledby={`passport-feed-${section.key}`}>
          <h3
            id={`passport-feed-${section.key}`}
            className="mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500"
          >
            {section.label}
          </h3>
          <div className="collector-grid">
            {section.items.map((item, idx) => (
              <FeedItem
                key={
                  item.kind === 'vehicle_batch'
                    ? `batch-${item.dayKey}-${idx}`
                    : item.encounter.public_id
                }
                item={item}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default PassportActivityFeed;
